import { z } from "zod";

import { PlanDetails } from "~data/PlanDetails";

import { activityTypeKeys } from "../../formatters/entities/SalesActivity";
import { zNoEmptyObject } from "../../utils";
import { zDay } from "../../utils/types";
import { Charge } from "../models/Charge";
import { Invoice } from "../models/Invoice";
import { Product } from "../models/partials/Product";
import { QuoteAdminSearchData } from "../models/Quote";
import { CobrowsingTeamUsage } from "../models/reporting/TeamUsage";
import { SalesOpportunityAdminSearchData } from "../models/SalesOpportunity";
import { Team } from "../models/Team";
import { TrackedEmail } from "../models/TrackedEmail";
import { ErrorResponse, Pagy, SuccessResponse } from "./standard-responses";

export const hqEndpoints = {
  "hq.accounts.relatedObjects.index": (p: { companyId: string }) => ["hq", "get", `/accounts/${p.companyId}/related`],
  "hq.teams.usage": (p: { companyId: string; teamId: string }) => [
    "hq",
    "get",
    `/accounts/${p.companyId}/teams/${p.teamId}/usage/data`,
  ],

  "hq.admins.emails.index": (p: { userId: string }) => ["hq", "get", `/admins/${p.userId}/emails/index`],
  "hq.admins.activities.counts": (p: { userId: string }) => ["hq", "get", `/admins/${p.userId}/activities/counts`],
  "hq.teams.merge": (p: { teamId: string }) => ["hq", "post", `/teams/${p.teamId}/merge`],
  "hq.teams.resetBilling": (p: { teamId: string }) => ["hq", "delete", `/teams/${p.teamId}/reset_billing`],
  "hq.teams.forceSync": (p: { teamId: string }) => ["hq", "post", `/teams/${p.teamId}/force_sync`],
  "hq.teams.update.onpremise": (p: { teamId: string }) => ["hq", "put", `/teams/${p.teamId}/onpremise`],
  "hq.teams.cancelSubscription": (p: { teamId: string }) => ["hq", "delete", `/teams/${p.teamId}/subscription`],
  "hq.teams.resumeSubscription": (p: { teamId: string }) => ["hq", "put", `/teams/${p.teamId}/subscription/on`],
  "hq.teams.adjustLicenses": (p: { teamId: string }) => ["hq", "post", `/teams/${p.teamId}/adjust_licenses`],
  "hq.teams.update.licenses": (p: { teamId: string }) => ["hq", "put", `/teams/${p.teamId}/licenses`],
  "hq.teams.update.delegateBilling": (p: { teamId: string }) => ["hq", "put", `/teams/${p.teamId}/delegate_billing`],
  "hq.teams.changePlan": (p: { teamId: string }) => ["hq", "put", `/teams/${p.teamId}/change_plan`],
  "hq.teams.cancelPlanChange": (p: { teamId: string }) => ["hq", "put", `/teams/${p.teamId}/cancel_plan_change`],
  "hq.teams.changeTrial": (p: { teamId: string }) => ["hq", "put", `/teams/${p.teamId}/change_trial`],
  "hq.teams.changeContract": (p: { teamId: string }) => ["hq", "put", `/teams/${p.teamId}/change_contract`],
  "hq.teams.billingHistory": (p: { teamId: string }) => ["hq", "get", `/teams/${p.teamId}/billing_history`],
  "hq.teams.pendingCharges": (p: { teamId: string }) => ["hq", "get", `/teams/${p.teamId}/invoice/pending`],
  "hq.teams.addToInvoice": (p: { teamId: string }) => ["hq", "post", `/teams/${p.teamId}/invoice/items`],
  "hq.teams.pendingCharge.delete": (p: { teamId: string; itemId: string }) => [
    "hq",
    "delete",
    `/teams/${p.teamId}/invoice/pending/${p.itemId}`,
  ],
  "hq.teams.invoice": (p: { teamId: string }) => ["hq", "post", `/teams/${p.teamId}/invoice`],
  "hq.teams.refundCharge": (p: { teamId: string; chargeId: string }) => [
    "hq",
    "post",
    `/teams/${p.teamId}/charges/${p.chargeId}`,
  ],
  "hq.teams.update.owner": (p: { teamId: string }) => ["hq", "put", `/teams/${p.teamId}/change_owner`],
} as const satisfies Record<string, (p: any) => ["hq", "get" | "delete" | "post" | "put", `/${string}`]>;

export const hqRequests = {
  "hq.accounts.relatedObjects.index": z.object({
    companyId: z.custom<`c_${string}`>(),
  }),
  "hq.teams.usage": z.object({
    companyId: z.custom<`c_${string}`>(),
    teamId: z.custom<`t_${string}`>(),
    from: z.coerce.date(),
    to: z.coerce.date(),
  }),
  "hq.admins.emails.index": z.object({
    userId: z.string(),
    page: z.number().optional().nullable(),
  }),
  "hq.admins.activities.counts": z.object({
    userId: z.string(),
  }),

  "hq.teams.merge": z.object({
    teamToDelete: z.string(),
    mainTeamId: z.custom<`t_${string}`>(),
  }),
  "hq.teams.resetBilling": z.object({
    teamId: z.custom<`t_${string}`>(),
  }),
  "hq.teams.forceSync": z.object({
    teamId: z.custom<`t_${string}`>(),
  }),
  "hq.teams.update.onpremise": z.object({
    teamId: z.custom<`t_${string}`>(),
    onPremise: z.boolean(),
    onPremiseRequireServerValidation: z.boolean(),
    onPremiseAllowedDomains: z.string().nullable(),
  }),
  "hq.teams.cancelSubscription": z.object({
    teamId: z.custom<`t_${string}`>(),
    now: z.literal(true).optional(),
  }),
  "hq.teams.resumeSubscription": z.object({
    teamId: z.custom<`t_${string}`>(),
  }),
  "hq.teams.adjustLicenses": z.object({
    teamId: z.custom<`t_${string}`>(),
  }),
  "hq.teams.update.licenses": z.object({
    teamId: z.custom<`t_${string}`>(),
    activeLicenses: z.number(),
    autoAdjustLicenses: Team.shape.autoAdjustLicenses,
    newMembersGracePeriodDays: z.number().min(0).max(31),
  }),

  "hq.teams.update.vat": z.object({
    teamId: z.custom<`t_${string}`>(),
    vatNumber: z.string().optional().nullable(),
  }),

  "hq.teams.update.delegateBilling": z.object({
    teamId: z.custom<`t_${string}`>(),
    billingOwner: z.string().nullable(),
  }),

  "hq.teams.changePlan": z.object({
    teamId: z.custom<`t_${string}`>(),
    plan: z.custom<PlanDetails>(),
    applyOn: zDay.nullable(),
    licenses: z.number().optional(),
  }),
  "hq.teams.cancelPlanChange": z.object({
    teamId: z.custom<`t_${string}`>(),
  }),
  "hq.teams.changeTrial": z.object({
    teamId: z.custom<`t_${string}`>(),
    trial: z
      .object({
        status: z.literal("continue"),
        endsOn: zDay,
        product: Product.optional(),
      })
      .or(
        z.object({
          status: z.enum(["convert", "terminate"]),
        }),
      ),
  }),
  "hq.teams.changeContract": z.object({
    teamId: z.custom<`t_${string}`>(),
    canMakePlanChanges: z.boolean(),
    contractTerminatesOn: zDay.nullable(),
    contractAutoRenews: z.boolean().nullable(),
  }),
  "hq.teams.billingHistory": z.object({
    teamId: z.custom<`t_${string}`>(),
  }),
  "hq.teams.pendingCharges": z.object({
    teamId: z.custom<`t_${string}`>(),
  }),
  "hq.teams.addToInvoice": z.object({
    teamId: z.custom<`t_${string}`>(),
    currency: z.enum(["usd", "eur", "gbp"]),
    amount: z.number(),
    description: z.string(),
  }),
  "hq.teams.pendingCharge.delete": z.object({
    teamId: z.custom<`t_${string}`>(),
    itemId: z.string(),
  }),
  "hq.teams.invoice": z.object({
    teamId: z.custom<`t_${string}`>(),
    dueAfter: zDay.optional(),
  }),
  "hq.teams.refundCharge": z.object({
    teamId: z.custom<`t_${string}`>(),
    chargeId: z.string(),
    amount: z.number(),
  }),
  "hq.teams.update.owner": z.object({
    teamId: z.custom<`t_${string}`>(),
    newOwnerId: z.string(),
  }),
} as const;

export const hqResponses = {
  "hq.accounts.relatedObjects.index": SuccessResponse.extend({
    invoices: Invoice.array(),
    quotes: QuoteAdminSearchData.array(),
    opportunities: SalesOpportunityAdminSearchData.array(),
  }).or(ErrorResponse.extend({ error: z.literal("record_not_found") })),
  "hq.teams.usage": SuccessResponse.extend({
    usage: CobrowsingTeamUsage,
    previousUsage: CobrowsingTeamUsage,
  }).or(
    ErrorResponse.extend({
      error: z.literal("record_not_found"),
    }),
  ),
  "hq.admins.emails.index": SuccessResponse.extend({
    emails: TrackedEmail.array(),
    pagy: Pagy,
  }),
  "hq.admins.activities.counts": SuccessResponse.extend({
    quarterEmailCount: z.number(),
    quarterActivityCount: z.record(z.enum(activityTypeKeys), z.number()),
    weekEmailCount: z.number(),
    weekActivityCount: z.record(z.enum(activityTypeKeys), z.number()),
  }),

  "hq.teams.merge": SuccessResponse,
  "hq.teams.resetBilling": SuccessResponse.or(
    ErrorResponse.extend({
      error: z.literal("already_customer"),
    }),
  ),
  "hq.teams.forceSync": SuccessResponse,
  "hq.teams.update.onpremise": SuccessResponse.or(
    ErrorResponse.extend({
      error: z.literal("validation_error"),
      teamErrors: zNoEmptyObject(z.object({ onPremiseAllowedDomains: z.string() })),
    }),
  ),
  "hq.teams.cancelSubscription": SuccessResponse.or(
    ErrorResponse.extend({
      error: z.literal("team_not_subscribed"),
    }),
  ),
  "hq.teams.resumeSubscription": SuccessResponse.or(
    ErrorResponse.extend({
      error: z.literal("team_not_cancelling"),
    }),
  ),
  "hq.teams.adjustLicenses": SuccessResponse,
  "hq.teams.update.licenses": SuccessResponse.or(
    ErrorResponse.extend({
      error: z.literal("validation_error"),
      teamErrors: zNoEmptyObject(z.object({ activeLicenses: z.string() })),
    }),
  ),

  "hq.teams.update.vat": SuccessResponse.or(
    ErrorResponse.extend({
      error: z.literal("validation_error"),
      teamErrors: zNoEmptyObject(z.object({ vatNumber: z.string() })),
    }),
  ),

  "hq.teams.update.delegateBilling": SuccessResponse.or(
    ErrorResponse.extend({
      error: z.literal("validation_error"),
      teamErrors: zNoEmptyObject(z.object({ billingOwner: z.string() })),
    }),
  ),
  "hq.teams.changePlan": SuccessResponse.or(
    ErrorResponse.extend({
      error: z.literal("missing_invoice_details"),
    }),
  ),
  "hq.teams.cancelPlanChange": SuccessResponse,
  "hq.teams.changeTrial": SuccessResponse.or(
    ErrorResponse.extend({
      error: z.literal("team_paying"),
    }),
  ),
  "hq.teams.changeContract": SuccessResponse.or(
    ErrorResponse.extend({
      error: z.literal("validation_error"),
      teamErrors: zNoEmptyObject(z.object({ canMakePlanChanges: z.string(), contractTerminatesOn: z.string() })),
    }),
  ),

  "hq.teams.billingHistory": SuccessResponse.extend({
    charges: Charge.array(),
    invoices: Invoice.array(),
  }),
  "hq.teams.pendingCharges": SuccessResponse.extend({
    lineItems: z
      .object({
        id: z.string(),
        amount: z.number(),
        currency: z.enum(["usd", "eur", "gbp"]),
        description: z.string(),
      })
      .array(),
  }),
  "hq.teams.addToInvoice": SuccessResponse,
  "hq.teams.pendingCharge.delete": SuccessResponse,
  "hq.teams.invoice": SuccessResponse,
  "hq.teams.refundCharge": SuccessResponse,
  "hq.teams.update.owner": SuccessResponse,
} as const;
